import {
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show
} from 'solid-js';
import { ark } from '@ark-ui/solid';
import { CheckCircleIcon, CopyIcon } from 'lucide-solid';
import { Button } from '~/components/ui/Button';
import { copyTextIntoClipboard, getUniqueId } from '~/utils/commonUtils.ts';
import { twMerge } from 'tailwind-merge';
import { COPY_SUCCESS_TIMEOUT } from '~/consts.ts';

type ReadOnlyFieldProps = {
  id?: string;
  label?: JSXElement;
  description?: string;
  value: string | number;
  width?: 'auto' | 'full';
  hintText?: JSXElement;
  dataTestId?: string;
  variant?: 'filled' | 'ghost';
  containerClass?: string;
  iconClass?: string;
};

export function ReadOnlyField(props: ReadOnlyFieldProps): JSXElement {
  const merged = mergeProps(
    {
      id: `readonly-field-${getUniqueId()}`,
      width: 'auto',
      dataTestId: `copy-${props.value}`,
      variant: 'filled'
    },
    props
  );
  const [copied, setCopied] = createSignal<boolean>(false);
  // const textCopy = getLocalizedString('help_texts.copy');
  // const textCopied = getLocalizedString('help_texts.copied');
  // const [copyTooltipText, setCopyTooltipText] = createSignal<string>(textCopy);
  let timerValue: ReturnType<typeof setTimeout>;

  createEffect(() => {
    if (copied()) {
      clearTimeout(timerValue);
      // setCopyTooltipText(textCopied);
      timerValue = setTimeout(() => {
        setCopied(false);
      }, COPY_SUCCESS_TIMEOUT);
    }
    // else {
    // setCopyTooltipText(textCopy);
    // }
  });

  const copyClickHandler = (e: Event) => {
    e.stopPropagation();
    copyTextIntoClipboard(String(merged.value)).then(
      () => !copied() && setCopied(true)
    );
  };

  const widthClass = twMerge(
    merged.width === 'full' ? 'w-full' : '',
    'max-w-full'
  );

  return (
    <div class={`flex flex-col items-start ${widthClass}`}>
      <Show when={merged.label}>
        <div class={`flex w-full justify-between`}>
          <div class={`flex flex-col`}>
            <label
              class={`mb-1 text-sm text-fg-default font-medium`}
              for={merged.id}
            >
              {merged.label}
            </label>
            <Show when={merged.description}>
              <span class={'text-sm mb-1.5 text-fg-subtle'}>
                {merged.description}
              </span>
            </Show>
          </div>
          <Show when={merged.hintText}>{merged.hintText}</Show>
        </div>
      </Show>
      {/*<Tooltip*/}
      {/*  content={copyTooltipText()}*/}
      {/*  id={`tooltip-${merged.id}`}*/}
      {/*  openDelay={0}*/}
      {/*  closeDelay={0}*/}
      {/*  triggerClass={`${widthClass} cursor-pointer`}*/}
      {/*>*/}
      <div
        data-testid={merged.dataTestId}
        class={twMerge(
          `relative ${widthClass} flex justify-between items-center pr-3 rounded-sm text-sm text-fg-muted cursor-pointer`,
          merged.containerClass
        )}
        classList={{
          'pl-2 py-2 bg-bg-subtle border border-e-bg-emphasized':
            merged.variant === 'filled'
        }}
        onClick={copyClickHandler}
        onMouseLeave={() => {
          // setTimeout(() => {
          //   setCopied(false);
          // }, 150);
        }}
      >
        <div
          class={`absolute top-0 left-0 right-0 bottom-0 z-1 bg-transparent`}
          onClick={copyClickHandler}
        ></div>
        <ark.input
          class={`font-code bg-transparent overflow-ellipsis`}
          style={{
            width: `${String(merged.value).length + 1}ch`
          }}
          id={merged.id}
          value={merged.value}
          disabled
        />
        <Button
          variant={`link`}
          type={`button`}
          onClick={copyClickHandler}
          class={`cursor-pointer`}
          classList={{
            'ml-2': merged.variant === 'filled'
          }}
        >
          {(() => {
            const iconProps = {
              class: twMerge(
                `text-fg-subtle relative left-1`,
                merged.iconClass
              ),
              width: 14,
              strokeWidth: 1
            };
            return (
              <Show
                when={copied()}
                fallback={<CopyIcon {...iconProps} />}
              >
                <CheckCircleIcon
                  {...iconProps}
                  strokeWidth={2}
                  class={twMerge(merged.iconClass, `text-success`)}
                />
              </Show>
            );
          })()}
        </Button>
      </div>
      {/*</Tooltip>*/}
    </div>
  );
}
